import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { useMutation } from '@tanstack/react-query';

import { exportLHC } from '../api';
import { captureLhcApiError } from '../utils/capture-lhc-api-error';

const downloadFile = (blob: Blob, filename: string): void => {
  const anchor = document.createElement('a');
  document.body.appendChild(anchor);

  const objectUrl = URL.createObjectURL(
    new Blob([blob], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
  );

  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.click();

  document.body.removeChild(anchor);
  URL.revokeObjectURL(objectUrl);
};

interface UseLHCExportOptions {
  onError?: (error: Error) => void;
  onSuccess?: () => void;
}

export const useLHCExportMutation = (options?: UseLHCExportOptions) => {
  const token = useAuthStore((s) => s.access_token);

  const exportMutation = useMutation({
    mutationFn: (id: string) => exportLHC(token, id),
    onSuccess: (data) => {
      downloadFile(data.data, data.filename);
      options?.onSuccess?.();
    },
    onError: (error: Error, lhcId: string) => {
      captureLhcApiError(error, {
        action: 'exportLhc',
        extras: { lhcId },
      });
      options?.onError?.(error);
    },
  });

  return {
    exportLHC: exportMutation.mutateAsync,
    isExporting: exportMutation.isPending,
    exportError: exportMutation.error,
  };
};
